import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

import DefaultLayout from "/home/runner/work/uxp-photoshop/uxp-photoshop/node_modules/@adobe/gatsby-theme-aio/src/components/MDXFilter/index.js";
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1 {...{
      "id": "writing-a-file-with-uxp"
    }}>{`Writing a file with UXP`}</h1>
    <p>{`In this tutorial, you will enhance your plugin by adding a button to export a `}<inlineCode parentName="p">{`.tsv`}</inlineCode>{` file that lists all your document's
layers including some details.`}</p>
    <h2 {...{
      "id": "prerequisites"
    }}>{`Prerequisites`}</h2>
    <p>{`This tutorial builds on top of the plugin from `}<a parentName="p" {...{
        "href": "../editing-the-document/index.md"
      }}>{`the previous tutorial`}</a>{`.`}</p>
    <h2 {...{
      "id": "step-1-adjust-the-indexhtml"
    }}>{`Step 1: Adjust the `}<inlineCode parentName="h2">{`index.html`}</inlineCode></h2>
    <p>{`In this tutorial, we want to add a new function to the plugin: Exporting a `}<inlineCode parentName="p">{`.tsv`}</inlineCode>{` file.`}</p>
    <p>{`To support this in your UI, adjust your `}<inlineCode parentName="p">{`index.html`}</inlineCode>{` file to include another section with another button `}<inlineCode parentName="p">{`<sp-button id="btnExport" />`}</inlineCode>{`:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-html"
      }}>{`<!DOCTYPE html>
<html>
  <head>
    <script src="index.js"></script>
  </head>
  <style>
    body {
      color: white;
      padding: 0 16px;
    }
  </style>
  <body>
    <sp-heading>Layers</sp-heading>
    <sp-heading size="S">Rename layers</sp-heading>
    <sp-body size="S"> Rename layers to contain their opacity. </sp-body>
    <sp-button id="btnRename">Rename Layers</sp-button>
    <sp-heading size="S">Export Report</sp-heading>
    <sp-body size="S"> Export a report of all layers in the document. </sp-body>
    <sp-button id="btnExport" variant="secondary">Export Report</sp-button>
  </body>
</html>
`}</code></pre>
    <p>{`The resulting panel looks like this:`}</p>
    <p><span parentName="p" {...{
        "className": "gatsby-resp-image-wrapper",
        "style": {
          "position": "relative",
          "display": "block",
          "marginLeft": "auto",
          "marginRight": "auto",
          "maxWidth": "250px"
        }
      }}>{`
      `}<span parentName="span" {...{
          "className": "gatsby-resp-image-background-image",
          "style": {
            "paddingBottom": "158.4%",
            "position": "relative",
            "bottom": "0",
            "left": "0",
            "display": "block",
            "transition": "opacity 0.5s 0.5s",
            "pointerEvents": "none"
          }
        }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
            "srcSet": ["/photoshop/uxp/static/77e260819f32dc151297beb95e846099/0e2a5/panel.webp 250w"],
            "sizes": "(max-width: 250px) 100vw, 250px",
            "type": "image/webp"
          }}></source>{`
          `}<source parentName="picture" {...{
            "srcSet": ["/photoshop/uxp/static/77e260819f32dc151297beb95e846099/63868/panel.png 250w"],
            "sizes": "(max-width: 250px) 100vw, 250px",
            "type": "image/png"
          }}></source>{`
          `}<img parentName="picture" {...{
            "className": "gatsby-resp-image-image",
            "src": "/photoshop/uxp/static/77e260819f32dc151297beb95e846099/63868/panel.png",
            "alt": "Photoshop panel with two buttons: \"Rename Layers\" and \"Export Report\"",
            "title": "Photoshop panel with two buttons: \"Rename Layers\" and \"Export Report\"",
            "loading": "lazy",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "opacity": "0",
              "transition": "opacity 0.5s",
              "color": "inherit",
              "boxShadow": "inset 0px 0px 0px 400px none",
              "top": "0",
              "left": "0"
            }
          }}></img>{`
        `}</picture>{`
    `}</span></p>
    <h2 {...{
      "id": "step-2-add-the-new-function-to-the-indexjs"
    }}>{`Step 2: Add the new function to the `}<inlineCode parentName="h2">{`index.js`}</inlineCode></h2>
    <p>{`To export the file, you need to do two things:`}</p>
    <ol>
      <li parentName="ol">{`create a string with the desired content of the file. This works the same as in the previous tutorials using the DOM APIs from the `}<inlineCode parentName="li">{`window.require('photoshop')`}</inlineCode>{` module.`}</li>
      <li parentName="ol">{`Use the UXP APIs, which you can access using `}<inlineCode parentName="li">{`window.require('uxp')`}</inlineCode>{`, to ask the user for a location to save the file and save a file with the desired content to that location.`}</li>
    </ol>
    <p>{`Add the following code to your existing `}<inlineCode parentName="p">{`index.js`}</inlineCode>{` file:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`// [...]

async function exportReport() {
    // create the TSV string
    let tsvString = "Base name\\tOpacity\\tIs visible";

    const app = window.require("photoshop").app;
    app.activeDocument.layers.forEach((layer) => {
        tsvString +=
            "\\n" +
            layer.name +
            "\\t" +
            layer.opacity +
            "\\t" +
            (layer.visible ? "yes" : "no");
    });

    // save the string to the filesystem
    const storage = window.require("uxp").storage;
    const file = await storage.localFileSystem.getFileForSaving("layers.tsv");
    await file.write(tsvString);
}

document.getElementById("btnExport").addEventListener("click", exportReport);
`}</code></pre>
    <p>{`Note the `}<inlineCode parentName="p">{`async`}</inlineCode>{` and `}<inlineCode parentName="p">{`await`}</inlineCode>{` keywords. We need them as the UXP storage APIs are asynchronous. We therefore need
to `}<inlineCode parentName="p">{`await`}</inlineCode>{` their execution.`}</p>
    <p>{`After reloading the plugin, you get presented with a file picker when clicking the `}<em parentName="p">{`"Export Report"`}</em>{` button:`}</p>
    <p><span parentName="p" {...{
        "className": "gatsby-resp-image-wrapper",
        "style": {
          "position": "relative",
          "display": "block",
          "marginLeft": "auto",
          "marginRight": "auto",
          "maxWidth": "725px"
        }
      }}>{`
      `}<span parentName="span" {...{
          "className": "gatsby-resp-image-background-image",
          "style": {
            "paddingBottom": "73.4375%",
            "position": "relative",
            "bottom": "0",
            "left": "0",
            "display": "block",
            "transition": "opacity 0.5s 0.5s",
            "pointerEvents": "none"
          }
        }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
            "srcSet": ["/photoshop/uxp/static/f02df016108715816990b16bec1289ce/cb523/save-file-dialog.webp 320w", "/photoshop/uxp/static/f02df016108715816990b16bec1289ce/797b9/save-file-dialog.webp 640w", "/photoshop/uxp/static/f02df016108715816990b16bec1289ce/8fd7a/save-file-dialog.webp 725w"],
            "sizes": "(max-width: 725px) 100vw, 725px",
            "type": "image/webp"
          }}></source>{`
          `}<source parentName="picture" {...{
            "srcSet": ["/photoshop/uxp/static/f02df016108715816990b16bec1289ce/72799/save-file-dialog.png 320w", "/photoshop/uxp/static/f02df016108715816990b16bec1289ce/6af66/save-file-dialog.png 640w", "/photoshop/uxp/static/f02df016108715816990b16bec1289ce/a0209/save-file-dialog.png 725w"],
            "sizes": "(max-width: 725px) 100vw, 725px",
            "type": "image/png"
          }}></source>{`
          `}<img parentName="picture" {...{
            "className": "gatsby-resp-image-image",
            "src": "/photoshop/uxp/static/f02df016108715816990b16bec1289ce/a0209/save-file-dialog.png",
            "alt": "A native Windows 10 file saving dialog with a file type \"TSV\" and a pre-selected name \"layers.tsv\"",
            "title": "A native Windows 10 file saving dialog with a file type \"TSV\" and a pre-selected name \"layers.tsv\"",
            "loading": "lazy",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "opacity": "0",
              "transition": "opacity 0.5s",
              "color": "inherit",
              "boxShadow": "inset 0px 0px 0px 400px none",
              "top": "0",
              "left": "0"
            }
          }}></img>{`
        `}</picture>{`
    `}</span></p>
    <p>{`Note that this uses the string passed into `}<inlineCode parentName="p">{`getFileForSaving(defaultName)`}</inlineCode>{` as the default name and also derives the supported file type from it.`}</p>
    <p>{`When you open the exported file in Excel, you can see the list of layers with their properties:`}</p>
    <p><span parentName="p" {...{
        "className": "gatsby-resp-image-wrapper",
        "style": {
          "position": "relative",
          "display": "block",
          "marginLeft": "auto",
          "marginRight": "auto",
          "maxWidth": "271px"
        }
      }}>{`
      `}<span parentName="span" {...{
          "className": "gatsby-resp-image-background-image",
          "style": {
            "paddingBottom": "33.210332103321036%",
            "position": "relative",
            "bottom": "0",
            "left": "0",
            "display": "block",
            "transition": "opacity 0.5s 0.5s",
            "pointerEvents": "none"
          }
        }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
            "srcSet": ["/photoshop/uxp/static/494fab272596c12a9757bb6eb6bc2868/a15fa/excel-tsv-preview.webp 271w"],
            "sizes": "(max-width: 271px) 100vw, 271px",
            "type": "image/webp"
          }}></source>{`
          `}<source parentName="picture" {...{
            "srcSet": ["/photoshop/uxp/static/494fab272596c12a9757bb6eb6bc2868/5fce1/excel-tsv-preview.png 271w"],
            "sizes": "(max-width: 271px) 100vw, 271px",
            "type": "image/png"
          }}></source>{`
          `}<img parentName="picture" {...{
            "className": "gatsby-resp-image-image",
            "src": "/photoshop/uxp/static/494fab272596c12a9757bb6eb6bc2868/5fce1/excel-tsv-preview.png",
            "alt": "Excel table with the columns \"Base name\", \"Opacity\", and \"Is visible\" and the layers as rows",
            "title": "Excel table with the columns \"Base name\", \"Opacity\", and \"Is visible\" and the layers as rows",
            "loading": "lazy",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "opacity": "0",
              "transition": "opacity 0.5s",
              "color": "inherit",
              "boxShadow": "inset 0px 0px 0px 400px none",
              "top": "0",
              "left": "0"
            }
          }}></img>{`
        `}</picture>{`
    `}</span></p>
    <h2 {...{
      "id": "next-steps"
    }}>{`Next steps`}</h2>
    <p>{`You now know how to use the APIs provided by UXP to interact with the file system. But UXP allows you to do a lot more: Performing network requests, saving preferences using `}<inlineCode parentName="p">{`localStorage`}</inlineCode>{`, get operating system information, open external urls, and much more. We encourage you to take a look at `}<a parentName="p" {...{
        "href": "/photoshop/uxp/src/pages/uxp/reference-js/index.md"
      }}>{`UXP's API Reference`}</a>{` to get an overview of everything you can do.`}</p>
    <p>{`To learn how to distribute your plugin, take a look at `}<a parentName="p" {...{
        "href": "/photoshop/uxp/src/pages/guides/distribution/index.md"
      }}>{`Sharing Your Plugin`}</a>{`.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      